import { Dashboard, FolderDetails, ImportExport, WorkflowAutomation } from "@carbon/icons-react";
import { SideNav, SideNavItems, SideNavLink, SideNavMenu, SideNavMenuItem } from "@carbon/react";
import React from "react";
import { Link, matchPath } from "react-router-dom";

import { AuthorizedMenu, AuthorizedItem } from "src/authentication/AuthorizedMenu.tsx";
import { Scope, ScopeResource } from "src/authentication/PermissionsProvider";
import { useT } from "src/internationalization";
import "./Sidebar.scss";

type SideBarProps = {
  showMenu: boolean;
  pathName: string;
};

const Sidebar: React.FC<SideBarProps> = ({ showMenu, pathName }) => {
  const t = useT();

  return (
    <SideNav
      aria-label="Side navigation"
      isRail={showMenu ? undefined : true}
      isFixedNav={showMenu ? true : undefined}
      isPersistent
      expanded={showMenu || undefined}
    >
      <SideNavItems isSideNavExpanded>
        <AuthorizedMenu
          menuItemsPermissions={[
            { resource: ScopeResource.ALERT, scopes: [Scope.VIEW] },
            { resource: ScopeResource.CASE, scopes: [Scope.VIEW] },
          ]}
        >
          <SideNavMenu
            isActive={pathName.includes("alerts") || pathName.includes("cases")}
            renderIcon={FolderDetails}
            title={t("sidebar.cases")}
            defaultExpanded={true}
            isSideNavExpanded={true}
          >
            <AuthorizedItem
              requiredPermissions={[{ resource: ScopeResource.ALERT, scopes: [Scope.VIEW] }]}
            >
              <Link className="no-decoration-link" to="/alerts/notAssigned">
                <SideNavMenuItem isActive={pathName.startsWith("/alert")}>
                  {t("sidebar.alerts")}
                </SideNavMenuItem>
              </Link>
            </AuthorizedItem>
            <AuthorizedItem
              requiredPermissions={[{ resource: ScopeResource.CASE, scopes: [Scope.VIEW] }]}
            >
              <Link className="no-decoration-link" to={"/cases"}>
                <SideNavMenuItem isActive={pathName.startsWith("/case")}>
                  {t("sidebar.cases")}
                </SideNavMenuItem>
              </Link>
            </AuthorizedItem>
          </SideNavMenu>
        </AuthorizedMenu>
        <AuthorizedMenu
          menuItemsPermissions={[
            { resource: ScopeResource.RULE, scopes: [Scope.VIEW] },
            { resource: ScopeResource.SCENARIO, scopes: [Scope.VIEW] },
            { resource: ScopeResource.BLACKLIST_ELEMENT, scopes: [Scope.VIEW] },
          ]}
        >
          <SideNavMenu
            renderIcon={WorkflowAutomation}
            title={t("sidebar.scoring")}
            defaultExpanded={true}
            isActive={["/rules", "/scenarios", "/blacklists"].includes(pathName)}
            isSideNavExpanded={true}
          >
            <AuthorizedItem
              requiredPermissions={[{ resource: ScopeResource.RULE, scopes: [Scope.VIEW] }]}
            >
              <Link className="no-decoration-link " to="/rules">
                <SideNavMenuItem
                  isActive={
                    !!matchPath("/rules", pathName) || !!matchPath("/rules/:code", pathName)
                  }
                >
                  {t("rules.title")}
                </SideNavMenuItem>
              </Link>
            </AuthorizedItem>
            <AuthorizedItem
              requiredPermissions={[{ resource: ScopeResource.SCENARIO, scopes: [Scope.VIEW] }]}
            >
              <Link className="no-decoration-link" to="/scenarios">
                <SideNavMenuItem isActive={pathName.startsWith("/scenario")}>
                  {t("scenarios.title")}
                </SideNavMenuItem>
              </Link>
            </AuthorizedItem>
            <AuthorizedItem
              requiredPermissions={[
                { resource: ScopeResource.BLACKLIST_ELEMENT, scopes: [Scope.VIEW] },
              ]}
            >
              <Link className="no-decoration-link" to={"/blacklists"}>
                <SideNavMenuItem isActive={pathName.startsWith("/blacklists")}>
                  {t("sidebar.blacklists")}
                </SideNavMenuItem>
              </Link>
            </AuthorizedItem>
          </SideNavMenu>
        </AuthorizedMenu>

        <SideNavLink renderIcon={Dashboard} isActive={pathName.startsWith("/reporting")}>
          <Link className="no-decoration-link" to="/reporting">
            {t("sidebar.reporting")}
          </Link>
        </SideNavLink>
        <AuthorizedMenu
          menuItemsPermissions={[
            {
              resource: ScopeResource.IMPORT,
              scopes: [Scope.VIEW],
            },
          ]}
        >
          <SideNavMenu
            renderIcon={ImportExport}
            title={t("sidebar.import")}
            defaultExpanded={true}
            isActive={pathName.startsWith("/import-export")}
            isSideNavExpanded={true}
          >
            <AuthorizedItem
              requiredPermissions={[
                {
                  resource: ScopeResource.IMPORT,
                  scopes: [Scope.VIEW],
                },
              ]}
            >
              <Link className="no-decoration-link" to={"/specifications"}>
                <SideNavMenuItem isActive={pathName.startsWith("/specifications")}>
                  {t("sidebar.import.specification")}
                </SideNavMenuItem>
              </Link>
              <Link className="no-decoration-link" to={"/jobs"}>
                <SideNavMenuItem isActive={pathName.startsWith("/job")}>
                  {t("sidebar.jobs")}
                </SideNavMenuItem>
              </Link>
              <Link className="no-decoration-link" to={"/import-export"}>
                <SideNavMenuItem isActive={pathName.endsWith("/import-export")}>
                  {t("sidebar.files")}
                </SideNavMenuItem>
              </Link>
            </AuthorizedItem>
          </SideNavMenu>
        </AuthorizedMenu>
      </SideNavItems>
    </SideNav>
  );
};

export default Sidebar;
